import { createAction, props } from '@ngrx/store';
import {
  RiskMitigationStrategyType,
  RiskDetails,
  InactiveRisk,
  RiskLevel,
  ControlAssetType,
  RiskAction,
  RiskActionDTO,
  RiskCoverage,
  RiskBudget,
  Risk,
  RiskLocalLawsComplianceCoverage,
  RiskContractualComplianceCoverage,
  RiskProjectedScore,
  RiskStatusName,
  RiskControlOptions,
  AddRiskDTO,
  SavePlanSimulationChangeDTO,
} from 'projects/shared/src/lib/core/interfaces';
import { baseProps } from 'projects/shared/src/lib/core/store/actions-base';

export const fetchRiskSimulation = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulation',
  baseProps<{
    planId: string;
    riskId?: string;
    strategy?: RiskMitigationStrategyType;
    optionId?: string;
  }>(),
);

export const fetchRiskSimulationSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationSuccess',
  baseProps<{
    risks: Risk[];
    addManualRiskAvailability: {
      allRisksExist: boolean;
      messageId: string | null;
    };
  }>(),
);

export const fetchRiskSimulationFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationFailure',
);

export const fetchRiskSimulationProjectedScore = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationProjectedScore',
  baseProps<{ planId: string }>(),
);

export const fetchRiskSimulationProjectedScoreSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationProjectedScoreSuccess',
  baseProps<{ projectedScore: RiskProjectedScore }>(),
);

export const fetchRiskSimulationProjectedScoreFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationProjectedScoreFailure',
);

export const fetchRiskSimulationRiskCoverage = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRiskCoverage',
  baseProps<{ planId: string }>(),
);

export const fetchRiskSimulationRiskCoverageSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRiskCoverageSuccess',
  baseProps<{ riskCoverage: RiskCoverage }>(),
);

export const fetchRiskSimulationRiskCoverageFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRiskCoverageFailure',
);

export const fetchRiskSimulationContractualCompliance = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationContractualCompliance',
  baseProps<{ planId: string }>(),
);

export const fetchRiskSimulationContractualComplianceSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationContractualComplianceSuccess',
  baseProps<{ contractualCompliance: RiskContractualComplianceCoverage }>(),
);

export const fetchRiskSimulationRegulatoryCompliance = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRegulatoryCompliance',
  baseProps<{ planId: string }>(),
);

export const fetchRiskSimulationRegulatoryComplianceSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRegulatoryComplianceSuccess',
  baseProps<{ regulatoryCompliance: RiskContractualComplianceCoverage }>(),
);

export const fetchRiskSimulationLocalLawsCompliance = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationLocalLawsCompliance',
  baseProps<{ planId: string }>(),
);

export const fetchRiskSimulationLocalLawsComplianceSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationLocalLawsComplianceSuccess',
  baseProps<{ localLawsCompliance: RiskLocalLawsComplianceCoverage }>(),
);

export const fetchRiskSimulationRiskBudget = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRiskBudget',
  baseProps<{ planId: string }>(),
);

export const fetchRiskSimulationRiskBudgetSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskSimulationRiskBudgetSuccess',
  baseProps<{ riskBudget: RiskBudget }>(),
);

export const changeRiskStrategy = createAction(
  '[@diso/security-plan-risks] changeRiskStrategy',
  baseProps<{
    strategy: RiskMitigationStrategyType;
    riskId: string;
    planId: string;
    optionId?: string;
  }>(),
);

export const changeRiskStrategySuccess = createAction(
  '[@diso/security-plan-risks] changeRiskStrategySuccess',
  baseProps<{ riskId: string; strategy: RiskMitigationStrategyType }>(),
);

export const changeRiskStrategyStatus = createAction(
  '[@diso/security-plan-risks] changeRiskStrategyStatus',
  baseProps<{
    status: RiskStatusName;
    riskId: string;
    planId: string;
    statusComment?: string;
  }>(),
);

export const changeRiskStrategyStatusSuccess = createAction(
  '[@diso/security-plan-risks] changeRiskStrategyStatusSuccess',
  baseProps<{ riskId: string; status: RiskStatusName }>(),
);

export const changeRiskStrategyStatusFailure = createAction(
  '[@diso/security-plan-risks] changeRiskStrategyStatusFailure',
  baseProps<{ riskId: string }>(),
);

export const revertRiskStatus = createAction(
  '[@diso/security-plan-risks] revertRiskStatus',
);

export const setSelectedRiskId = createAction(
  '[@diso/security-plan-risks] setSelectedRiskId',
  baseProps<{ riskId: string | null }>(),
);

export const focusRisk = createAction(
  '[@diso/security-plan-risks] focusRisk',
  baseProps<{ riskId: string | null }>(),
);

export const showRisksSidebar = createAction(
  '[@diso/security-plan-risks] showRisksSidebar',
  baseProps<{ currentStep: number; riskId?: string }>(),
);

export const hideRisksSidebar = createAction(
  '[@diso/security-plan-risks] hideRisksSidebar',
);

export const resetRisksSidebar = createAction(
  '[@diso/security-plan-risks] resetRisksSidebar',
);

export const fetchAllInactiveRisks = createAction(
  '[@diso/security-plan-risks] fetchAllInactiveRisks',
);

export const fetchAllInactiveRisksSuccess = createAction(
  '[@diso/security-plan-risks] fetchAllInactiveRisksSuccess',
  baseProps<{ inactiveRisks: InactiveRisk[] }>(),
);

export const fetchAllInactiveRisksFailure = createAction(
  '[@diso/security-plan-risks] fetchAllInactiveRisksFailure',
);

export const fetchRiskDetails = createAction(
  '[@diso/security-plan-risks] fetchRiskDetails',
  baseProps<{
    riskId: string;
    planId: string;
    riskTemplateId: string;
    riskTemplateName: string;
  }>(),
);

export const fetchRiskDetailsSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskDetailsSuccess',
  baseProps<{ riskDetails: RiskDetails }>(),
);

export const fetchRiskDetailsFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskDetailsFailure',
);

export const fetchRiskControls = createAction(
  '[@diso/security-plan-risks] fetchRiskControls',
  baseProps<{ riskId: string }>(),
);

export const fetchRiskControlsSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskControlsSuccess',
  baseProps<{ riskControls: RiskControlOptions }>(),
);

export const fetchRiskControlsFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskControlsFailure',
  baseProps(),
);

export const fetchRiskLevels = createAction(
  '[@diso/security-plan-risks] fetchRiskLevels',
  baseProps<{ riskId: string }>(),
);

export const fetchRiskLevelsSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskLevelsSuccess',
  baseProps<{
    riskLevels: {
      impact: RiskLevel;
      likelihood: RiskLevel;
    };
  }>(),
);

export const fetchRiskLevelsFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskLevelsFailure',
);

export const fetchAssetTypes = createAction(
  '[@diso/security-plan-risks] fetchAssetTypes',
  baseProps<{ controlIds: string[] }>(),
);

export const fetchAssetTypesSuccess = createAction(
  '[@diso/security-plan-risks] fetchAssetTypesSuccess',
  baseProps<{ assetTypes: ControlAssetType }>(),
);

export const fetchAssetTypesFailure = createAction(
  '[@diso/security-plan-risks] fetchAssetTypesFailure',
  baseProps(),
);

export const fetchRiskActions = createAction(
  '[@diso/security-plan-risks] fetchRiskActions',
  baseProps<{ riskActionDTO: RiskActionDTO }>(),
);

export const fetchRiskActionsSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskActionsSuccess',
  baseProps<{ riskActions: RiskAction[] }>(),
);

export const fetchRiskActionsFailure = createAction(
  '[@diso/security-plan-risks] fetchRiskActionsFailure',
  baseProps(),
);

export const fetchRiskActionsByRiskId = createAction(
  '[@diso/security-plan-risks] fetchRiskActionsByRiskId',
  baseProps<{ riskId: string }>(),
);

export const fetchRiskActionsByRiskIdSuccess = createAction(
  '[@diso/security-plan-risks] fetchRiskActionsByRiskIdSuccess',
  baseProps<{ riskActions: RiskAction[] }>(),
);

export const showSolutionMappingModal = createAction(
  '[@diso/security-plan-risks] showSolutionMappingModal',
  baseProps<{ optionId?: string }>(),
);

export const hideSolutionMappingModal = createAction(
  '[@diso/security-plan-risks] hideSolutionMappingModal',
);

export const addRisk = createAction(
  '[@diso/security-plan-risks] addRisk',
  baseProps<{ riskDto: AddRiskDTO }>(),
);

export const addRiskSuccess = createAction(
  '[@diso/security-plan-risks] addRiskSuccess',
  baseProps<{ riskId: string }>(),
);

export const addRiskFailure = createAction(
  '[@diso/security-plan-risks] addRiskFailure',
  baseProps<{ error: any }>(),
);

export const setEditMode = createAction(
  '[@diso/security-plan-risks] setEditMode',
  baseProps<{ editMode: boolean }>(),
);

export const setChangingData = createAction(
  '[@diso/security-plan-risks] setChangingData',
  baseProps<{
    changingData?: SavePlanSimulationChangeDTO;
    highlightRims?: boolean;
  }>(),
);

export const savePlanSimulationChange = createAction(
  '[@diso/security-plan-risks] savePlanSimulationChange',
  baseProps<{ savePlanSimulationChangeDto: SavePlanSimulationChangeDTO }>(),
);

export const savePlanSimulationChangeSuccess = createAction(
  '[@diso/security-plan-risks] savePlanSimulationChangeSuccess',
);

export const savePlanSimulationChangeFailure = createAction(
  '[@diso/security-plan-risks] savePlanSimulationChangeFailure',
);

export const highlightRimsForRisk = createAction(
  '[@diso/security-plan-risks] highlightRimsForRisk',
  baseProps<{ riskId: string; openQSelection?: boolean }>(),
);

export const clearRimsHighlight = createAction(
  '[@diso/security-plan-risks] clearRimsHighlight',
);

export const resetRiskFlowState = createAction(
  '[@diso/security-plan-risks] resetRiskFlowState',
);

export const resetRisks = createAction(
  '[@diso/security-plan-risks] resetRisks',
);

export const setCurrentStep = createAction(
  '[@diso/security-plan-risks] setCurrentStep',
  props<{ step: number }>(),
);

export const refreshActionMapping = createAction(
  '[@diso/security-plan-risks] refreshActionMapping',
  baseProps<{ actionId: string; riskActionDTO: RiskActionDTO }>(),
);

export const refreshActionMappingSuccess = createAction(
  '[@diso/security-plan-risks] refreshActionMappingSuccess',
  baseProps<{ actions: RiskAction[]; actionId: string }>(),
);

export const refreshActionMappingFailure = createAction(
  '[@diso/security-plan-risks] refreshActionMappingFailure',
);

export const setRefreshActionId = createAction(
  '[@diso/security-plan-risks] setRefreshActionId',
  baseProps<{ actionId: string }>(),
);

export const clearRefreshActionId = createAction(
  '[@diso/security-plan-risks] clearRefreshActionId',
  baseProps<{ actionId: string }>(),
);
